import { Button } from "@/components/UI/Button";
import { ExecutionRequestStatus } from "@/lib/interfaces and Enums/ENUMExecutionRequestStatus";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { fetchRequestWrapper } from "@/lib/utilities";
import useTableUIStore from "@/store/tableUIStore";
import savedPanelsStore, { IPanelCounterparty } from "@/store/savedPanelsStore";
import showDesksStore from "@/store/showDesksStore";
import tradeDataStore from "@/store/tradeDataStore";
import tradeSettingsStore from "@/store/tradeSettingsStore";
import {
  setTradeSettingsInvalidCounterparties,
  setTradeSettingsSelectedCounterparties,
} from "@/store/tradeSettingsStoreACTIONS";
import { useAuth0 } from "@auth0/auth0-react";
import { FormControl, MenuItem, Select } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Tooltip } from "react-tooltip";
import { cubeCustomColors } from "../../../../../../tailwind.config";
import { getGridSelectColors } from "@/lib/styles/gridSelectStyles";
import { IToggleCounterpartiesButton } from "../../helper/tradeSettingsHelper/tradeSettingsInterfaces";
import { IReactSelectSavePanel } from "./ReactSelectSavedPanel";
import { findThisTradeSettings } from "./SelectPanelHelper";
import {
  compareArraysOfCounterparties,
  updateListOfSettingsCPsBasedOnEndpoint,
} from "./CPListHelper";
import {
  getCellElementHeight,
  getCellFontSizeRem,
  getFontSizeClass,
} from "../../helper/actionSummaryHelper";

const CPList = (props: IReactSelectSavePanel) => {
  const { data, theme, cryptoID, isEditPanel } = props;
  const rowHeight = useTableUIStore((state) => state.rowHeight);
  const fontSizeSetting = useTableUIStore((state) => state.fontSize);
  const cellElementHeight = getCellElementHeight(rowHeight);
  const cellFontSize = getCellFontSizeRem(fontSizeSetting);
  const fontSizeClass = getFontSizeClass(fontSizeSetting);
  const gridSelectColors = getGridSelectColors(theme);

  // Zustand Trade Data
  const tradeDataFromStore = tradeDataStore((state) => state.tradeData);
  const currentStoreTrade = tradeDataFromStore.find(
    (es) => es.esid === data.esid,
  );

  // --- Trade Settings - from store.
  const allTradeSettings = tradeSettingsStore(
    (state) => state.tradeSettingsData,
  );
  const [thisTradeSettings, setThisTradeSettings] = useState(
    findThisTradeSettings({ esid: data.esid }),
  );
  useEffect(() => {
    setThisTradeSettings(findThisTradeSettings({ esid: data.esid }));
  }, [allTradeSettings]);
  // --- End Trade Settings.

  const [validCounterpartiesList, setValidCounterpartiesList] = useState<
    IToggleCounterpartiesButton[]
  >([]);

  const [counterpartiesReturned, setCounterpartiesReturned] =
    useState<boolean>(false);

  // Get the panel data for the users saved settings
  const panelsData = savedPanelsStore((state) => state.userPanels);

  // counterparties fetched via the /Counterparties/{esid} - Done via an immediately invoked function expression (IIFE)
  const esidForFetch = currentStoreTrade?.esid ?? data.esid ?? "";
  const { getAccessTokenSilently } = useAuth0();

  const [showDesks, setShowDesks] = useState(false);
  const allDesksShowing = showDesksStore((state) => state.showingDesksList);

  // Order trades can only be sent to 1 counterparty (edit panel reads type off the trade, new trades off the toggle)
  const isOrderMode = isEditPanel
    ? currentStoreTrade?.type === "Order"
    : (thisTradeSettings?.tradeTypes?.find((t) => t.name === "Order")
        ?.active ?? false);

  useEffect(() => {
    if (esidForFetch !== "") {
      (async () => {
        const fetchedCounterParties = await fetchRequestWrapper(
          { url: `/CounterParties/${esidForFetch}` },
          getAccessTokenSilently,
        );

        setTradeSettingsInvalidCounterparties({
          esid: data.esid,
          invalidCounterpartyList: fetchedCounterParties.invalidCounterparties,
        });

        setValidCounterpartiesList(fetchedCounterParties.validCounterparties);
        setCounterpartiesReturned(true);
      })();
    }

    return () => {
      const initialState = showDesksStore.getState().showingDesksList;
      const newState = initialState.filter(
        (obj) =>
          (obj.ESID === data.esid && obj.widgetID !== cryptoID) ||
          obj.ESID !== data.esid,
      );
      showDesksStore.getState().setShowingDeskList(newState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Need to call for valid CPs on each expand of the trade at new.
  // Perform a comparison to the stored settings and the incoming - If there is a difference, update the settings.
  useEffect(() => {
    if (
      validCounterpartiesList &&
      validCounterpartiesList.length > 0 &&
      thisTradeSettings &&
      thisTradeSettings.counterpartiesList.length > 0
    ) {
      const areCounterpartiesEqual = compareArraysOfCounterparties(
        validCounterpartiesList,
        thisTradeSettings.counterpartiesList,
      );

      if (!areCounterpartiesEqual) {
        // If there is a difference between current counterparties in settings and the newly returned, alter the settings.
        updateListOfSettingsCPsBasedOnEndpoint(
          thisTradeSettings,
          validCounterpartiesList,
          data.esid,
        );
      }
    }
  }, [validCounterpartiesList]);

  async function getCounterpartyDesks() {
    const fetchedCounterParties = await fetchRequestWrapper(
      { url: `/Counterparties/${esidForFetch}/desks` },
      getAccessTokenSilently,
    );

    const newCpList = thisTradeSettings?.counterpartiesList.map((cp) => {
      const fetched = fetchedCounterParties.validCounterparties.find(
        (f: IToggleCounterpartiesButton) => f.code === cp.code,
      );
      const desks = fetched?.desks;
      return {
        ...cp,
        desks: desks,
        activeDesks: cp.activeDesks
          ? cp.activeDesks
          : cp.active && !cp.desks
            ? desks
            : [],
      };
    });
    // Need to look at adding the CP's to the Zustand store.
    if (newCpList) {
      setTradeSettingsSelectedCounterparties({
        esid: data.esid,
        newCounterpartyList: newCpList,
      });
    }
  }

  useEffect(() => {
    if (showDesks) {
      getCounterpartyDesks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDesks]);

  useEffect(() => {
    const inAllDesksShowing = allDesksShowing.some(
      (showingDesksObj) =>
        data.esid === showingDesksObj.ESID &&
        cryptoID === showingDesksObj.widgetID,
    );
    setShowDesks(inAllDesksShowing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDesksShowing]);

  // Watch for trade type changes - clear CPs when switching to Order mode with >1 CP selected
  useEffect(() => {
    if (!thisTradeSettings || isEditPanel) {
      return; // Skip for edit panel - type is already set on the trade
    }

    const isOrderMode =
      thisTradeSettings.tradeTypes?.find((t) => t.name === "Order")?.active ??
      false;
    const activeCPs = thisTradeSettings.counterpartiesList?.filter(
      (cp) => cp.active,
    );

    if (isOrderMode && activeCPs && activeCPs.length > 1) {
      // Clear all CP selections when switching to Order with multiple CPs
      const clearedCPList = thisTradeSettings.counterpartiesList.map((cp) => ({
        ...cp,
        active: false,
        activeDesks: [],
      }));
      setTradeSettingsSelectedCounterparties({
        esid: data.esid,
        newCounterpartyList: clearedCPList,
      });
      toast.warning(
        "Order trades can only be sent to 1 counterparty. Counterparty selection has been cleared.",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thisTradeSettings?.tradeTypes]);

  function updateSelectedCounterparties(cp: IToggleCounterpartiesButton) {
    let tempCPList: IToggleCounterpartiesButton[] | undefined;

    if (isOrderMode) {
      // Order mode: Only one CP can be selected at a time
      // If clicking the already active CP, deselect it
      // If clicking a different CP, deselect all others and select this one
      tempCPList = thisTradeSettings?.counterpartiesList.map((e) => {
        if (cp.code === e.code) {
          // Toggle the clicked CP
          if (cp.active) {
            return { ...e, active: false, activeDesks: [] };
          } else {
            return {
              ...e,
              active: true,
              activeDesks: e.desks ? [...e.desks] : [],
            };
          }
        } else {
          // Deselect all other CPs
          return { ...e, active: false, activeDesks: [] };
        }
      });
    } else {
      // RFQ mode: Multiple CPs can be selected (existing behavior)
      tempCPList = thisTradeSettings?.counterpartiesList.map((e) => {
        if (cp.code === e.code) {
          if (cp.active) {
            //will get set to innactive. Clear the active desks
            return {
              ...cp,
              active: !cp.active,
              activeDesks: [],
            };
          } else {
            return {
              ...cp,
              active: !cp.active,
              activeDesks: cp.desks ? [...cp.desks] : [], // Was null - Check the availablity of []
            };
          }
        }
        return e;
      });
    }

    // Send New CP list to the trade settings rather than Hook.
    if (tempCPList) {
      setTradeSettingsSelectedCounterparties({
        esid: data.esid,
        newCounterpartyList: tempCPList,
      });
    }
    //setCounterpartiesList(tempCPList);
  }

  function setPanelFromExistingTrade() {
    const statusThatNeedToBeExcluded = [
      ExecutionRequestStatus.Recalled,
      ExecutionRequestStatus.NotDealt,
    ];

    const arrOfCPSelected = data.executionRequestCounterParties
      .filter((cp: any) => !statusThatNeedToBeExcluded.includes(cp.status))
      .map((cp: any) => ({
        code: cp.provider,
        activeDesks:
          cp.deskRestrictions && cp.deskRestrictions.length > 0
            ? cp.deskRestrictions
            : null,
      }));

    const newArray = validCounterpartiesList.map((cp) => {
      const matchedCP = arrOfCPSelected.find(
        (dataCP: { code: string; activeDesks: string[] | null }) =>
          dataCP.code === cp.code,
      );

      if (matchedCP) {
        return {
          ...cp,
          active: true,
          activeDesks: matchedCP.activeDesks,
        };
      }
      return { ...cp };
    });
    // Send New CP list to the trade settings rather than Hook.
    setTradeSettingsSelectedCounterparties({
      esid: data.esid,
      newCounterpartyList: newArray,
    });
    // setCounterpartiesList(newArray);
  }

  function handleSelectDesk(cp: IToggleCounterpartiesButton, item: string) {
    const newCp = thisTradeSettings?.counterpartiesList.map((e) => {
      if (cp.code === e.code) {
        if (cp.activeDesks === undefined || cp.activeDesks.length === 0) {
          return {
            ...cp,
            active: true,
            activeDesks: [item],
          };
        } else if (cp.activeDesks.length > 0) {
          if (cp.activeDesks.includes(item)) {
            //remove desk from activeDesks link
            const activeDesksCopy = cp.activeDesks.filter(
              (desk) => desk !== item,
            );
            return {
              ...cp,
              active: activeDesksCopy.length > 0, //if there is at least one desk selected, counterparty should be active
              activeDesks: activeDesksCopy,
            };
          } else
            return {
              //not in list => add to list and return new array
              ...cp,
              active: true,
              activeDesks: [...cp.activeDesks, item],
            };
        }
      } else if (
        isOrderMode &&
        (e.active || (e.activeDesks && e.activeDesks.length > 0))
      ) {
        // Order trades can only be sent to 1 counterparty - picking a desk on another CP clears this one
        return { ...e, active: false, activeDesks: [] };
      }
      return e;
    });
    // Send New CP list to the trade settings rather than Hook.
    if (newCp) {
      setTradeSettingsSelectedCounterparties({
        esid: data.esid,
        newCounterpartyList: newCp,
      });
    }
    // setCounterpartiesList(newCp);
  }

  // Check if BOTH the counterparties AND the panel have returned - then update the fav.
  const favouriteSettingApplied = useRef(false);

  useEffect(() => {
    applyFavouritesToSelectPanel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counterpartiesReturned, panelsData]);

  function applyFavouritesToSelectPanel() {
    if (favouriteSettingApplied.current) {
      return; //Early return.
    }
    if (data.status !== ExecutionStrategyStatus.New) {
      if (counterpartiesReturned) {
        setPanelFromExistingTrade();
        favouriteSettingApplied.current = true;
        return;
      }
      return; //Early return.
    }
    const checkIfFavourite = panelsData.find((panel) => panel.favourite);

    // Check if in Order mode - Orders can only have 1 counterparty
    // Default to false (RFQ behavior) if tradeTypes not available yet
    const isOrderMode =
      thisTradeSettings?.tradeTypes?.find((t) => t.name === "Order")?.active ??
      false;

    // Check if no CPs are actively selected (list may have items but none selected)
    const hasNoActiveCP = !thisTradeSettings?.counterpartiesList?.some(
      (cp) => cp.active,
    );

    if (counterpartiesReturned && hasNoActiveCP) {
      favouriteSettingApplied.current = true;

      // Check if favourite panel should be applied
      const favouriteCPs = checkIfFavourite?.counterparties;
      const shouldSkipFavourite =
        isOrderMode && favouriteCPs && favouriteCPs.length > 1;

      if (checkIfFavourite && favouriteCPs && !shouldSkipFavourite) {
        const updatedValidCounterparties = validCounterpartiesList.map((cp) => {
          const savedCp = (favouriteCPs as IPanelCounterparty[]).find(
            (s) => s.code === cp.code,
          );
          if (!savedCp) return cp;
          return {
            ...cp,
            active: true,
            activeDesks: savedCp.desks,
          };
        });
        setTradeSettingsSelectedCounterparties({
          esid: data.esid,
          newCounterpartyList: updatedValidCounterparties,
        });
        return;
      }

      // Default: no CPs selected (either no favourite, or Order mode with multi-CP favourite)
      setTradeSettingsSelectedCounterparties({
        esid: data.esid,
        newCounterpartyList: validCounterpartiesList,
      });
    }
  }

  return (
    <div
      data-testid="CPListContainer"
      id="CPListContainer"
      className="CPContainer my-2 mr-3 h-full max-h-[135px] overflow-auto"
    >
      <div id="panel" className="flex items-center"></div>
      <div className="flex h-fit w-full flex-wrap gap-2 ">
        {counterpartiesReturned ? (
          thisTradeSettings &&
          thisTradeSettings.counterpartiesList.length > 0 ? (
            thisTradeSettings.counterpartiesList.map((cp) => (
              <div className="flex flex-row" key={cp.code}>
                <Button
                  variant={cp.active ? "mainBlueAction" : "secondaryBlueAction"}
                  id={cp.code.toString().replace(/\s+/g, "") + "SelectorCPList"} //Desks: ${cp.activeDesks.join(", ")}
                  className={fontSizeClass}
                  onClick={() => updateSelectedCounterparties(cp)}
                  data-tooltip-id="cp-button-tooltip"
                  data-tooltip-html={`${cp.name}${cp.activeDesks && cp.activeDesks.length > 0 ? `<br/>${cp.activeDesks.map((d) => `- ${d}`).join("<br/>")}` : ""}`}
                  size={"button_md_CP"}
                >
                  {cp.code}
                  {(cp.activeDesks &&
                    cp.desks &&
                    cp.activeDesks.length > 0 &&
                    cp.activeDesks.length !== cp.desks.length) ||
                  (!cp.desks && cp.activeDesks && cp.activeDesks.length > 0)
                    ? ` (${cp.activeDesks.length}) `//"*"
                    : ""}
                </Button>
                {showDesks ? (
                  <FormControl sx={{ width: 40 }}>
                    <Select
                      className="p-0"
                      style={{ height: `${cellElementHeight}px` }}
                      id={`${cp.code}SelectDesksDropdown`}
                      inputProps={{ "aria-label": "Without label" }}
                      value={[]}
                      multiple
                      sx={{
                        ...gridSelectColors.control,
                        "& .MuiSelect-icon": gridSelectColors.icon,
                      }}
                      MenuProps={{
                        sx: {
                          "& .MuiMenu-paper": {
                            paddingTop: "0px !important",
                            paddingBottom: "0px !important",
                            width: "160px",
                            ...gridSelectColors.menuPaper,
                          },
                          "& .MuiMenuItem-root": {
                            minWidth: "120px !important",
                            width: "max !important",
                            height: `${cellElementHeight + 3}px`,
                            fontSize: cellFontSize,
                            color: gridSelectColors.menuPaper.color,
                          },
                          "& .MuiMenuItem-root:hover": gridSelectColors.menuItemHover,
                        },
                      }}
                    >
                      {cp.desks?.map((item) => (
                        <MenuItem
                          key={item}
                          // value={item}
                          onClick={() => handleSelectDesk(cp, item)}
                          sx={{
                            backgroundColor:
                              cp.activeDesks &&
                              cp.activeDesks.length > 0 &&
                              cp.activeDesks.includes(item)
                                ? theme === "dark"
                                  ? cubeCustomColors.cubeBrand[200]
                                  : cubeCustomColors.cubeBrand[100]
                                : "",
                            color:
                              cp.activeDesks &&
                              cp.activeDesks.length > 0 &&
                              cp.activeDesks.includes(item)
                                ? theme === "dark"
                                  ? "black"
                                  : ""
                                : theme === "dark"
                                  ? "#BABABA"
                                  : "",
                          }}
                        >
                          <div title={item}>
                            {item}
                            {cp.activeDesks &&
                            cp.activeDesks.length > 0 &&
                            cp.activeDesks.includes(item)
                              ? " ✓ "
                              : ""}
                          </div>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : null}
              </div>
            ))
          ) : (
            <div className="flex h-16 w-full flex-col items-center justify-center">
              <div className="">
                No available counterparties for this Execution Strategy
              </div>
            </div>
          )
        ) : (
          <div className="flex w-full flex-col items-center justify-center">
            <div className="flex w-full justify-center">
              Loading available counterparties
            </div>
          </div>
        )}
      </div>

      {thisTradeSettings &&
      thisTradeSettings?.inValidCounterpartiesList.length > 0 ? (
        <div className="pb-2">
          <div className="p-1">Invalid Counterparties:</div>
          <div className="flex h-fit w-full flex-wrap gap-2 ">
            {counterpartiesReturned ? (
              thisTradeSettings &&
              thisTradeSettings.inValidCounterpartiesList.length > 0 ? (
                thisTradeSettings.inValidCounterpartiesList.map((cp) => (
                  <Button
                    disabled={true}
                    variant={
                      cp.active
                        ? "mainBlueAction"
                        : "secondaryBlueActionNoHover"
                    }
                    className={`cursor-not-allowed ${fontSizeClass}`}
                    key={cp.code}
                    size={"button_md_CP"}
                    title={cp.message}
                  >
                    {cp.code}
                  </Button>
                ))
              ) : (
                <div className="flex h-16 w-full flex-col items-center justify-center">
                  <div className="">
                    No invalid counterparties for this Execution Strategy
                  </div>
                </div>
              )
            ) : (
              <div className="flex w-full flex-col items-center">
                <div className="">Loading available counterparties</div>
              </div>
            )}
          </div>
        </div>
      ) : null}
      <Tooltip
        id="cp-button-tooltip"
        place="bottom"
        className="z-50 !text-xs !shadow-lg"
        style={{
          backgroundColor: theme === "dark" ? "#171717" : "rgb(209 213 219)",
          color: theme === "dark" ? "white" : "black",
          padding: "0 4px",
        }}
      />
    </div>
  );
};

export default CPList;
